/**
 * Loadout Selector - Allows players to select their starting joker for matches
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useWallet } from '@solana/wallet-adapter-react';
import { http } from '../../api/http';

interface BaseJoker {
  id: string;
  name: string;
  description: string;
  rarity: string;
  effects: Array<{ kind: string; [key: string]: unknown }>;
}

interface PlayerLoadout {
  wallet: string;
  selectedStartingJoker: string | null;
  updatedAt: string;
}

interface LoadoutSelectorProps {
  className?: string;
}

export const LoadoutSelector: React.FC<LoadoutSelectorProps> = ({ className }) => {
  const { publicKey } = useWallet();
  const wallet = publicKey?.toBase58();

  const [availableJokers, setAvailableJokers] = useState<BaseJoker[]>([]);
  const [selectedJoker, setSelectedJoker] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // Fetch available jokers
  useEffect(() => {
    const fetchJokers = async () => {
      try {
        const response = await http.get<{ jokers: BaseJoker[] }>('/loadouts/jokers/available');
        setAvailableJokers(response.jokers);
      } catch (error) {
        console.error('Failed to fetch available jokers:', error);
      }
    };
    fetchJokers();
  }, []);

  // Fetch player's current loadout
  useEffect(() => {
    if (!wallet) {
      setSelectedJoker(null);
      return;
    }

    const fetchLoadout = async () => {
      setLoading(true);
      try {
        const response = await http.get<{ loadout: PlayerLoadout }>(`/loadouts/${wallet}`);
        setSelectedJoker(response.loadout.selectedStartingJoker);
      } catch (error) {
        console.error('Failed to fetch loadout:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLoadout();
  }, [wallet]);

  const handleSelectJoker = async (jokerId: string | null) => {
    if (!wallet) return;

    setSaving(true);
    try {
      const response = await http.post<{ loadout: PlayerLoadout }>(`/loadouts/${wallet}`, {
        startingJoker: jokerId,
      });
      setSelectedJoker(response.loadout.selectedStartingJoker);
    } catch (error) {
      console.error('Failed to save loadout:', error);
    } finally {
      setSaving(false);
    }
  };

  const selectedJokerData = availableJokers.find((j) => j.id === selectedJoker);

  if (!wallet) {
    return null;
  }

  return (
    <div className={`rounded-xl border border-white/10 bg-black/40 p-3 ${className ?? ''}`}>
      {/* Header - Clickable to expand/collapse */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">🃏</span>
          <div className="text-left">
            <div className="font-arcade text-xs uppercase tracking-wide text-purple-400">
              Starting Loadout
            </div>
            {loading ? (
              <div className="text-xs text-white/50">Loading...</div>
            ) : selectedJokerData ? (
              <div className="text-sm text-white font-semibold">{selectedJokerData.name}</div>
            ) : (
              <div className="text-xs text-white/50">No joker selected</div>
            )}
          </div>
        </div>
        <motion.span animate={{ rotate: expanded ? 180 : 0 }} className="text-white/50">
          ▼
        </motion.span>
      </button>

      {/* Expanded joker selection */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="text-xs text-white/50 mb-2">
                Select a base joker to start matches with:
              </div>

              {/* No joker option */}
              <button
                type="button"
                onClick={() => handleSelectJoker(null)}
                disabled={saving}
                className={`w-full mb-2 rounded-lg border px-3 py-2 text-left transition ${
                  selectedJoker === null
                    ? 'border-purple-400/50 bg-purple-500/20'
                    : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
              >
                <div className="text-sm font-semibold text-white">None</div>
                <div className="text-xs text-white/50">Start with no joker</div>
              </button>

              {/* Joker options */}
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {availableJokers.map((joker) => {
                  const isSelected = selectedJoker === joker.id;
                  return (
                    <button
                      key={joker.id}
                      type="button"
                      onClick={() => handleSelectJoker(joker.id)}
                      disabled={saving}
                      className={`w-full rounded-lg border px-3 py-2 text-left transition ${
                        isSelected
                          ? 'border-purple-400/50 bg-purple-500/20'
                          : 'border-white/10 bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-white">{joker.name}</span>
                        <span
                          className={`text-[10px] uppercase px-1.5 py-0.5 rounded ${
                            joker.rarity === 'legendary'
                              ? 'bg-amber-500/20 text-amber-300'
                              : joker.rarity === 'rare'
                                ? 'bg-blue-500/20 text-blue-300'
                                : 'bg-white/10 text-white/50'
                          }`}
                        >
                          {joker.rarity}
                        </span>
                      </div>
                      <div className="text-xs text-white/50 mt-0.5 line-clamp-2">
                        {joker.description}
                      </div>
                      {isSelected && (
                        <div className="mt-1 text-[10px] text-purple-300">✓ Selected</div>
                      )}
                    </button>
                  );
                })}
              </div>

              {saving && <div className="mt-2 text-xs text-purple-300 text-center">Saving...</div>}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
